/// <amd-module name="WorkItemsHub/ZeroData/WorkItemTrackingZeroDataUtils" />
import * as React from "react";
import * as Resources from "WorkItemsHub/Resources";
import { ZeroData, IZeroDataProps, ZeroDataActionType} from "VSSUI/ZeroData";
import { WorkZeroDataIllustrationPaths } from "WorkItemsHub/ZeroData/IllustrationUrlUtils";
import { getAssetLocation } from "VSS/Platform/Location";

export interface ILink {
    href: string;
    linkText: string;
}

/**
 * Creates Zero Day component instance for "Assigned to me" page.
 * @param alternativeInfoLink alternative info link
 */
export function createForAssignedToMe(pageContext: any, alternativeInfoLink: ILink = null): JSX.Element {
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_AssignedToMe_PrimaryMessage,
        secondaryText: Resources.ZeroData_AssignedToMe_SecondaryMessage,
        actionHref: alternativeInfoLink ? alternativeInfoLink.href : Resources.ZeroData_AssignedToMe_SecondaryMessageLinkUrl,
        actionText: alternativeInfoLink ? alternativeInfoLink.linkText: Resources.ZeroData_SecondaryMessageLinkText,
        actionType: ZeroDataActionType.link,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.YourWorkInOnePlace),
        imageAltText: Resources.Illustrations_YourWorkInOnePlaceAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates Zero Day component instance for Following page.
 * @param alternativeInfoLink alternative info link
 */
export function createForFollowing(pageContext: any, alternativeInfoLink: ILink = null): JSX.Element {
    const secondaryMessageParts: string[] = Resources.ZeroData_Following_SecondaryMessageFormat.split("{0}");
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_Following_PrimaryMessage,
        secondaryText: (
                <span>
                    {secondaryMessageParts[0]}
                    <span className="bowtie-icon bowtie-watch-eye"></span>
                    {secondaryMessageParts[1]}
                </span>
            ),
        actionHref: alternativeInfoLink ? alternativeInfoLink.href : Resources.ZeroData_Following_SecondaryMessageLinkUrl,
        actionText: alternativeInfoLink ? alternativeInfoLink.linkText: Resources.ZeroData_Following_SecondaryMessageLinkText,            
        actionType: ZeroDataActionType.link,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.KeepAnEyeOnImportantWork),
        imageAltText: Resources.Illustrations_KeepAnEyeOnImportantWorkAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates Zero Day component instance for Following page when follows is not configured.
 * @param alternativeInfoLink alternative info link
 */
export function createForFollowingNotconfigured(pageContext: any, alternativeInfoLink: ILink = null): JSX.Element {
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_Following_NotConfigured_PrimaryMessage,
        secondaryText: Resources.ZeroData_Following_NotConfigured_SecondaryMessage,
        actionHref: alternativeInfoLink ? alternativeInfoLink.href : Resources.ZeroData_Following_NotConfigured_SecondaryMessageLinkUrl,
        actionText: alternativeInfoLink ? alternativeInfoLink.linkText: Resources.ZeroData_Following_NotConfigured_SecondaryMessageLinkText,        
        actionType: ZeroDataActionType.link,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.KeepAnEyeOnImportantWork),
        imageAltText: Resources.Illustrations_KeepAnEyeOnImportantWorkAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates Zero Day component instance for "My activity" page.
 * @param alternativeInfoLink alternative info link
 */
export function createForMyActivity(pageContext: any, alternativeInfoLink: ILink = null): JSX.Element {
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_MyActivity_PrimaryMessage,
        secondaryText: Resources.ZeroData_MyActivity_SecondaryMessage,
        actionHref: alternativeInfoLink ? alternativeInfoLink.href : Resources.ZeroData_MyActivity_SecondaryMessageLinkUrl,
        actionText: alternativeInfoLink ? alternativeInfoLink.linkText: Resources.ZeroData_SecondaryMessageLinkText,  
        actionType: ZeroDataActionType.link,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.GetBackToRecentWork),
        imageAltText: Resources.Illustrations_GetBackToRecentWorkAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates Zero Day component instance for Mentioned page.
 * @param alternativeInfoLink alternative info link
 */
export function createForMentioned(pageContext: any, alternativeInfoLink: ILink = null): JSX.Element {
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_Mentioned_PrimaryMessage,
        secondaryText: Resources.ZeroData_Mentioned_SecondaryMessage,
        actionHref: alternativeInfoLink ? alternativeInfoLink.href : Resources.ZeroData_Mentioned_SecondaryMessageLinkUrl,
        actionText: alternativeInfoLink ? alternativeInfoLink.linkText: Resources.ZeroData_Mentioned_SecondaryMessageLinkText,  
        actionType: ZeroDataActionType.link,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.MentionSomeone),
        imageAltText: Resources.Illustrations_MentionSomeoneAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates Zero Day component instance for "Recently created" page.
 * @param alternativeInfoLink alternative info link
 */
export function createForRecentlyCreated(pageContext: any, alternativeInfoLink: ILink = null): JSX.Element {
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_RecentlyCreated_PrimaryMessage,
        secondaryText: Resources.ZeroData_RecentlyCreated_SecondaryMessage,
        actionHref: alternativeInfoLink ? alternativeInfoLink.href : Resources.ZeroData_RecentlyCreated_SecondaryMessageLinkUrl,
        actionText: alternativeInfoLink ? alternativeInfoLink.linkText: Resources.ZeroData_SecondaryMessageLinkText,  
        actionType: ZeroDataActionType.link,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.GetBackToRecentWork),
        imageAltText: Resources.Illustrations_GetBackToRecentWorkAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates Zero Day component instance for "Recently completed" page.
 * @param alternativeInfoLink alternative info link
 */
export function createForRecentlyCompleted(pageContext: any, alternativeInfoLink: ILink = null): JSX.Element {
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_RecentlyCompleted_PrimaryMessage,
        secondaryText: Resources.ZeroData_RecentlyCompleted_SecondaryMessage,
        actionHref: alternativeInfoLink ? alternativeInfoLink.href : Resources.ZeroData_RecentlyCompleted_SecondaryMessageLinkUrl,
        actionText: alternativeInfoLink ? alternativeInfoLink.linkText: Resources.ZeroData_SecondaryMessageLinkText,  
        actionType: ZeroDataActionType.link,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.GetBackToRecentWork),
        imageAltText: Resources.Illustrations_GetBackToRecentWorkAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates Zero Day component instance for "Recently updated" page.
 * @param alternativeInfoLink alternative info link
 */
export function createForRecentlyUpdated(pageContext: any, alternativeInfoLink: ILink = null): JSX.Element {
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_RecentlyUpdated_PrimaryMessage,
        secondaryText: Resources.ZeroData_RecentlyUpdated_SecondaryMessage,
        actionHref: alternativeInfoLink ? alternativeInfoLink.href : Resources.ZeroData_RecentlyUpdated_SecondaryMessageLinkUrl,
        actionText: alternativeInfoLink ? alternativeInfoLink.linkText: Resources.ZeroData_SecondaryMessageLinkText,  
        actionType: ZeroDataActionType.link,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.GetBackToRecentWork),
        imageAltText: Resources.Illustrations_GetBackToRecentWorkAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates Zero Day component instance for "my teams" page.
 * @param alternativeInfoLink alternative info link
 */
export function createForMyTeams(pageContext: any, alternativeInfoLink: ILink = null): JSX.Element {
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_MyTeams_PrimaryMessage,
        secondaryText: Resources.ZeroData_MyTeams_SecondaryMessage,
        actionHref: alternativeInfoLink ? alternativeInfoLink.href : Resources.ZeroData_MyTeams_SecondaryMessageLinkUrl,
        actionText: alternativeInfoLink ? alternativeInfoLink.linkText: Resources.ZeroData_SecondaryMessageLinkText,         
        actionType: ZeroDataActionType.link,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.GetBackToRecentWork),
        imageAltText: Resources.Illustrations_GetBackToRecentWorkAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates Zero Day component instance for my teams page when user is not a member of any team
 */
export function createForMyTeamsNotconfigured(pageContext: any): JSX.Element {
    const zeroDataProps: IZeroDataProps = {
        primaryText: Resources.ZeroData_MyTeams_NotConfigured_PrimaryMessage,
        secondaryText: Resources.ZeroData_MyTeams_NotConfigured_SecondaryMessage,
        imagePath: getAssetLocation(pageContext, WorkZeroDataIllustrationPaths.KeepAnEyeOnImportantWork),
        imageAltText: Resources.Illustrations_KeepAnEyeOnImportantWorkAltText
    };
    return <ZeroData {...zeroDataProps} />;
}

/**
 * Creates ZeroData component instance for general server error.
 * @param message server error message
 */
export function createForServerError(errorMessage: string): JSX.Element {
    return <ZeroData
        primaryText={Resources.ZeroData_ServerError_PrimaryText}
        secondaryText={errorMessage}
        imagePath={""}
        imageAltText={Resources.Illustrations_SomethingWrongOnServerAltText} />;
}
